import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Loader2, ImageOff, Package } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const getAuthHeaders = () => {
  const token = localStorage.getItem("session_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export default function ProductPickerDialog({ open, onOpenChange, conversationId, onSent }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(null);

  useEffect(() => {
    if (!open) return;
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API}/api/products`, {
          headers: getAuthHeaders(), credentials: "include",
        });
        if (res.ok) {
          const data = await res.json();
          setProducts(data.filter((p) => p.active !== false));
        }
      } catch (e) {
        toast.error("Error al cargar productos");
      } finally {
        setLoading(false);
      }
    })();
  }, [open]);

  const send = async (p) => {
    if (!conversationId) return toast.error("Abre un chat primero");
    setSending(p.product_id);
    try {
      const res = await fetch(`${API}/api/products/${p.product_id}/send-to-chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        credentials: "include",
        body: JSON.stringify({ conversation_id: conversationId }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Error al enviar");
      }
      const msg = await res.json();
      toast.success("Producto enviado");
      onSent && onSent(msg);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSending(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="w-5 h-5 text-emerald-400" /> Enviar producto al chat
          </DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="flex items-center justify-center py-8 text-slate-400">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Cargando catálogo…
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-8 text-slate-400">
            No tienes productos activos. Crea uno desde tu panel para venderlo en el chat.
          </div>
        ) : (
          <div className="max-h-[60vh] overflow-y-auto space-y-2 pr-1">
            {products.map((p) => (
              <button
                key={p.product_id}
                onClick={() => send(p)}
                disabled={sending === p.product_id}
                className="w-full flex items-center gap-3 p-2 rounded-lg bg-slate-800 hover:bg-slate-700 transition text-left disabled:opacity-50"
              >
                <div className="w-14 h-14 rounded-md bg-slate-900 flex items-center justify-center flex-shrink-0 overflow-hidden">
                  {p.image_url
                    ? <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" />
                    : <ImageOff className="w-6 h-6 text-slate-600" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{p.name}</p>
                  {p.description && <p className="text-xs text-slate-400 truncate">{p.description}</p>}
                  <p className="text-emerald-400 text-sm font-semibold mt-0.5">
                    {p.currency} {Number(p.price).toFixed(2)}
                  </p>
                </div>
                {sending === p.product_id && <Loader2 className="w-4 h-4 animate-spin text-emerald-400" />}
              </button>
            ))}
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-slate-300">Cerrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
